/**
 * Cliente HTTP de Zen
 *
 * La baseURL se construye DINÁMICAMENTE en cada request usando el schema del tenant activo.
 * En dev, el apiClient rutea las llamadas al backend correcto según activeTenantSchema,
 * independientemente del subdominio del frontend. Esto permite al usuario cambiar de tenant
 * sin cambiar de subdominio (evita problemas de localStorage cross-origin).
 *
 * Ejemplos:
 *   activeTenantSchema = 'mi_empresa'  → API en http://mi-empresa.localhost:8000/api/v1
 *   activeTenantSchema = 'zenlatam'   → API en http://zenlatam.localhost:8000/api/v1
 *   Prod: mismo dominio, nginx hace el proxy
 */
import { marcarErrorMostrado } from '@/lib/erroresMostrados'
import { recordarDestino } from '@/lib/destinoTrasLogin'
import { useAuthStore } from '@/store/authStore'
import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios'

const API_PORT = import.meta.env.VITE_API_PORT || '8000'
const IS_DEV = import.meta.env.DEV

/**
 * Retorna la baseURL correcta en tiempo de ejecución.
 * Se llama en cada request via interceptor.
 * En dev usa activeTenantSchema para construir el subdominio del backend correcto.
 */
export function getApiBaseUrl(): string {
  // Si hay una URL explícita configurada, usarla (staging/prod con dominio fijo)
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL
  }

  const { protocol, hostname } = window.location

  if (IS_DEV) {
    const { activeTenantSchema } = useAuthStore.getState()
    if (activeTenantSchema) {
      // Extraer el dominio base (ej: "localhost" de "mi-empresa.localhost")
      const baseDomain = hostname.includes('.') ? hostname.split('.').slice(1).join('.') : hostname
      // El schema usa underscores (mi_empresa) pero el dominio usa guiones (mi-empresa)
      const subdomain = activeTenantSchema.replace(/_/g, '-')
      return `${protocol}//${subdomain}.${baseDomain}:${API_PORT}/api/v1`
    }
    // Sin tenant activo, usar el hostname actual
    return `${protocol}//${hostname}:${API_PORT}/api/v1`
  }

  // Prod: nginx hace el proxy /api → backend en el mismo dominio/puerto
  return `${protocol}//${hostname}/api/v1`
}

// ─── Instancia de Axios (sin baseURL fija) ────────────────────────────────────
const apiClient: AxiosInstance = axios.create({
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
  withCredentials: true,  // Send httpOnly cookies (refresh token) with every request
})

// Endpoints públicos que NO deben enviar Authorization (evita 401 por token expirado)
const PUBLIC_AUTH_PATHS = [
  '/auth/token/',
  '/auth/password-reset/',
  '/auth/accept-invitation/',
  '/auth/sso-config/',
  '/auth/google/',
  '/auth/workspace-lookup/',
]

// ─── Interceptor de Request ───────────────────────────────────────────────────
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const { accessToken } = useAuthStore.getState()

    // Construir baseURL dinámicamente en cada request
    config.baseURL = getApiBaseUrl()

    if (accessToken) {
      const isPublic = PUBLIC_AUTH_PATHS.some((p) => config.url?.startsWith(p))
      if (!isPublic) {
        config.headers.Authorization = `Bearer ${accessToken}`
      }
    }

    return config
  },
  (error) => Promise.reject(error)
)

// ─── Interceptor de Response: refresh automático ─────────────────────────────
let isRefreshing = false
let failedQueue: Array<{ resolve: (token: string) => void; reject: (err: unknown) => void }> = []

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error)
    else prom.resolve(token!)
  })
  failedQueue = []
}

// ─── Refresh coordinado ENTRE PESTAÑAS (Web Locks) ───────────────────────────
// El backend usa ROTATE_REFRESH_TOKENS + BLACKLIST_AFTER_ROTATION: cada uso del
// refresh token genera uno nuevo y blacklistea el anterior. Con varias pestañas
// abiertas, si dos refrescan a la vez usan el MISMO refresh token → una lo rota y
// blacklistea el que la otra está usando → 401 → logout espurio. Es el caso típico
// tras un DEPLOY: el backend reinicia, todas las pestañas fallan sus requests y
// refrescan al mismo tiempo. Web Locks serializa el refresh POR ORIGEN (todas las
// pestañas comparten el lock): solo una refresca a la vez; las siguientes esperan
// y usan la cookie ya rotada, sin pisarse.
const REFRESH_LOCK = 'zen-auth-token-refresh'

type LockGranted = () => Promise<unknown>
interface LockMgrLike {
  request(name: string, options: { mode: 'exclusive' | 'shared' }, cb: LockGranted): Promise<unknown>
}

async function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  const nav = navigator as unknown as { locks?: LockMgrLike }
  if (nav.locks && typeof nav.locks.request === 'function') {
    return nav.locks.request(REFRESH_LOCK, { mode: 'exclusive' }, fn as LockGranted) as Promise<T>
  }
  // Navegador sin Web Locks API: degradar a refresh sin coordinación entre pestañas.
  return fn()
}

/**
 * Refresca el access token vía la cookie httpOnly de refresh, COORDINADO entre
 * pestañas (solo una refresca a la vez por origen). Devuelve el nuevo access token
 * o lanza el error del refresh. El llamador decide si desloguear — ver
 * `shouldLogoutOnRefreshError`.
 */
export async function performTokenRefresh(): Promise<string> {
  return withRefreshLock(async () => {
    const baseUrl = getApiBaseUrl()
    // Refresh token viaja automáticamente en la cookie httpOnly (withCredentials).
    // Timeout acotado para no dejar el lock tomado si el backend no responde.
    const response = await axios.post(
      `${baseUrl}/auth/token/refresh/`,
      {},
      { withCredentials: true, timeout: 15000 },
    )
    const access = response.data?.access as string | undefined
    if (!access) throw new Error('El refresh no devolvió access token.')
    return access
  })
}

/**
 * True si el fallo del refresh es DEFINITIVO (sesión irrecuperable) y corresponde
 * desloguear + mandar al login:
 *   - 401/403: refresh token inválido, expirado o blacklisteado.
 *   - 400: el endpoint de refresh no recibió un refresh token válido → la cookie
 *     httpOnly expiró o desapareció (browser abierto > 7 días). Antes este caso NO
 *     deslogueaba y la app quedaba "viva" sin datos (todas las API en 401 sin
 *     redirigir). Es un fallo definitivo, no transitorio.
 * Un error de red, timeout o 5xx es TRANSITORIO (backend caído durante un deploy)
 * → NO desloguear: la sesión sigue en memoria y se recupera al volver el backend.
 */
export function shouldLogoutOnRefreshError(err: unknown): boolean {
  if (axios.isAxiosError(err)) {
    const status = err.response?.status
    return status === 400 || status === 401 || status === 403
  }
  return false
}

/** Código de error de negocio (`error.code`) de una respuesta del backend. */
function errorCode(err: unknown): string | undefined {
  if (axios.isAxiosError(err)) {
    return (err.response?.data as { error?: { code?: string } } | undefined)?.error?.code
  }
  return undefined
}

/**
 * Redirige al login con recarga completa. Determinístico: no depende de que un
 * guard de ruta se vuelva a renderizar (la causa de que, al expirar la sesión, la
 * app quedara sin redirigir). Loop-safe: no redirige si ya estamos en una página
 * de auth. `reason` alimenta el mensaje del login ('expired' | 'session_superseded').
 */
let _redirectingToLogin = false
export function redirectToLogin(reason?: string) {
  if (_redirectingToLogin) return
  const path = window.location.pathname
  if (path.startsWith('/auth/') || path.startsWith('/colaboradores/')) return
  _redirectingToLogin = true
  // Después de volver a entrar, de vuelta a la pantalla en la que estaba (#1656).
  recordarDestino(path + window.location.search)
  const q = reason ? `?reason=${encodeURIComponent(reason)}` : ''
  window.location.replace(`/auth/login${q}`)
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    if (error.response?.status === 401 && !originalRequest._retry) {
      // Sesión única: el token pertenece a una sesión ya reemplazada (login en
      // otro dispositivo). No sirve refrescar (el refresh dará el mismo 401) →
      // expulsar directo con el mensaje correcto.
      if (errorCode(error) === 'SESSION_SUPERSEDED') {
        useAuthStore.getState().logout()
        redirectToLogin('session_superseded')
        return Promise.reject(error)
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`
            return apiClient(originalRequest)
          })
          .catch((err) => Promise.reject(err))
      }

      originalRequest._retry = true
      isRefreshing = true

      const { setTokens, logout } = useAuthStore.getState()

      try {
        const access = await performTokenRefresh()
        setTokens(access)

        // Actualizar permisos del tenant activo desde el backend (JWT es slim, no incluye permisos).
        // Llamada fire-and-forget: no bloquea el request original.
        const baseUrl = getApiBaseUrl()
        const { activeTenantSchema, tenants, setTenants } = useAuthStore.getState()
        if (activeTenantSchema) {
          axios.get(`${baseUrl}/auth/me/`, {
            withCredentials: true,
            headers: { Authorization: `Bearer ${access}` },
          }).then(meRes => {
            const activeTenantData = meRes.data?.active_tenant
            if (activeTenantData && tenants.length > 0) {
              const updatedTenants = tenants.map(t =>
                t.schema === activeTenantData.schema
                  ? { ...t, permissions: activeTenantData.permissions, disabled_modules: activeTenantData.disabled_modules }
                  : t
              )
              setTenants(updatedTenants)
            }
          }).catch(() => { /* permisos del store siguen vigentes */ })
        }

        processQueue(null, access)
        originalRequest.headers.Authorization = `Bearer ${access}`
        return apiClient(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError, null)
        // Desloguear + redirigir al login solo ante un fallo DEFINITIVO del refresh
        // (400 cookie ausente/expirada, 401/403 token inválido). Ante un fallo
        // transitorio (backend caído durante un deploy, red, timeout) mantener la
        // sesión: la pestaña se recupera al volver el backend, sin echar al usuario.
        if (shouldLogoutOnRefreshError(refreshError)) {
          logout()
          const reason = errorCode(refreshError) === 'SESSION_SUPERSEDED'
            ? 'session_superseded' : 'expired'
          redirectToLogin(reason)
        }
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  }
)

/**
 * Refresca el access token usando el refresh token (cookie httpOnly) y lo guarda
 * en el store. Devuelve el nuevo access token, o null si el refresh falla.
 *
 * Reutilizable fuera del interceptor de 401 — en particular por el cliente
 * WebSocket, que debe conectar con un JWT vigente: reconectar con un token
 * expirado provoca un handshake 403 en bucle (el backend cierra la conexión
 * sin aceptar el upgrade).
 */
export async function refreshAccessToken(): Promise<string | null> {
  try {
    const access = await performTokenRefresh()
    useAuthStore.getState().setTokens(access)
    return access
  } catch {
    return null
  }
}

export default apiClient

/**
 * Construye una URL absoluta del backend (para referencia, no para descarga directa).
 */
export function apiDownloadUrl(path: string): string {
  const base = getApiBaseUrl()
  const cleanPath = path.startsWith('/') ? path : `/${path}`
  return `${base}${cleanPath}`
}

/** El `filename` de un `Content-Disposition`, o `null` si no viene. */
export function nombreDeContentDisposition(header: string | undefined): string | null {
  if (!header) return null
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header)
  if (utf8) return decodeURIComponent(utf8[1].trim())
  const simple = /filename="?([^";]+)"?/i.exec(header)
  return simple ? simple[1].trim() : null
}

/**
 * Descarga un archivo del backend con autenticación JWT.
 * Crea un blob URL y dispara la descarga en el browser.
 *
 * `usarNombreDelServidor`: el nombre lo decide el backend (`Content-Disposition`)
 * y `filename` queda de respaldo. Sirve cuando el nombre es parte del formato que
 * exige un organismo —el LRE de la DT va como `{rut}_{aaaamm}.csv`— y el
 * navegador no tiene los datos para armarlo.
 */
export async function apiDownload(
  path: string, filename: string,
  opts?: { usarNombreDelServidor?: boolean; timeout?: number },
): Promise<void> {
  const res = await apiClient.get(path, { responseType: 'blob', timeout: opts?.timeout })
  const nombre = (opts?.usarNombreDelServidor
    && nombreDeContentDisposition(res.headers?.['content-disposition'])) || filename
  const url = window.URL.createObjectURL(new Blob([res.data]))
  const link = document.createElement('a')
  link.href = url
  link.download = nombre
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

// ─── Helpers tipados ──────────────────────────────────────────────────────────
export interface ApiError {
  error: {
    code: string
    message: string
    details: Record<string, string[]>
  }
}

export function isApiError(error: unknown): error is AxiosError<ApiError> {
  return axios.isAxiosError(error) && error.response?.data?.error !== undefined
}

export function getApiErrorMessage(error: unknown, opts?: { marcar?: boolean }): string {
  // Extraer el mensaje para mostrarlo cuenta como «ya se le avisó al usuario»:
  // la red de seguridad global lo lee de erroresMostrados y no duplica el aviso.
  // Solo la propia red pasa `marcar: false` (se suprimiría a sí misma).
  if (opts?.marcar !== false) marcarErrorMostrado(error)
  if (isApiError(error)) {
    return error.response?.data.error.message || 'Ha ocurrido un error inesperado.'
  }
  return 'Ha ocurrido un error inesperado.'
}

export function getApiFieldErrors(error: unknown): Record<string, string[]> {
  if (isApiError(error)) {
    return error.response?.data.error.details || {}
  }
  return {}
}
